import { API_PATHS, AUTH_REFRESH_PATH, USERS_CURRENT_PATH } from '@treneo/shared/constants';
import { logger } from '@treneo/shared/logger';
import {
  getAuthMetadataFromLocalStorage,
  saveAuthMetadataToLocalStorage
} from '@treneo/shared/utils';

import { getApiClient } from './core';

export class AuthStatusChecker {
  private listeners: Map<string, Set<() => void>> = new Map();

  on(event: string, handler: () => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event);
    if (set) {
      set.add(handler);
    }
  }

  off(event: string, handler: () => void): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  private emit(event: string): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(h => h());
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const metadata = getAuthMetadataFromLocalStorage();
    const now = Date.now();

    if (metadata && now - metadata.lastCheck < 300000) {
      return metadata.isAuthenticated;
    }

    try {
      const apiClient = getApiClient();
      const response = await apiClient.get(`${USERS_CURRENT_PATH}?includePermissions=true`);
      const isAuthenticated = response.status === 200;

      saveAuthMetadataToLocalStorage({
        isAuthenticated,
        lastCheck: now
      });

      return isAuthenticated;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        saveAuthMetadataToLocalStorage({
          isAuthenticated: false,
          lastCheck: now
        });
        this.emit('refresh:error');
        return false;
      }
      return metadata?.isAuthenticated ?? false;
    }
  }

  async isTokenExpiringSoon(bufferMinutes = 5): Promise<boolean> {
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get<{ isExpiringSoon: boolean }>(
        `${API_PATHS.AUTH_CHECK_EXPIRY}?buffer=${bufferMinutes}`
      );

      if (response.status === 200 && response.data) {
        return response.data.isExpiringSoon;
      }

      return true;
    } catch (error) {
      logger.warn('Failed to check if token is expiring soon:', error);
      return true;
    }
  }

  startProactiveTokenRefresher(intervalMs = 60000) {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const checkAndRefresh = async () => {
      const metadata = getAuthMetadataFromLocalStorage();
      if (!metadata?.isAuthenticated) {
        return;
      }

      if (document.visibilityState === 'hidden') {
        return;
      }

      const expiring = await this.isTokenExpiringSoon(2);
      if (expiring) {
        try {
          const apiClient = getApiClient();
          await apiClient.post(AUTH_REFRESH_PATH);
          saveAuthMetadataToLocalStorage({
            isAuthenticated: true,
            lastCheck: Date.now()
          });
        } catch (e) {
          logger.warn('Proactive refresh failed', e);
          this.emit('refresh:error');
        }
      }
    };

    const intervalId = setInterval(checkAndRefresh, intervalMs);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }
}

export const authStatusChecker = new AuthStatusChecker();
